import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { GraduationCap, PlayCircle, Layers, Award } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyCourses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [enrolling, setEnrolling] = useState(null);
  const [orderCourse, setOrderCourse] = useState(null);
  const [orderDone, setOrderDone] = useState(false);
  const [ordering, setOrdering] = useState(false);

  const load = () => {
    api.get("/my-courses").then((r) => setItems(r.data || [])).catch(() => setItems([]));
    axios.get(`${API}/api/public/courses`).then((r) => setCatalog(r.data || [])).catch(() => setCatalog([]));
  };
  useEffect(() => { load(); }, []);

  const enrollFree = async (c) => {
    setEnrolling(c.id);
    try {
      await api.post(`/my-courses/${c.id}/enroll-free`);
      toast.success("¡Inscripción exitosa! 🎉");
      load();
      navigate(`/dashboard/my-courses/${c.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setEnrolling(null);
    }
  };

  const requestCourse = async () => {
    setOrdering(true);
    try {
      await axios.post(`${API}/api/public/courses/${orderCourse.id}/order`, {
        name: user?.name || user?.email,
        email: user?.email,
        whatsapp: user?.whatsapp || null,
      });
      setOrderDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando la solicitud");
    } finally {
      setOrdering(false);
    }
  };

  if (items === null || catalog === null) return <div className="text-sm text-slate-500">Cargando…</div>;
  const ownedIds = new Set(items.map((c) => c.id));
  const explore = catalog.filter((c) => !ownedIds.has(c.id));

  return (
    <div className="space-y-6" data-testid="my-courses-page">
      <PageHeader title="Cursos On-Demand" subtitle="Estudiá a tu propio ritmo: continuá tus cursos o descubrí nuevos." />
      <Tabs defaultValue={items.length > 0 ? "mine" : "explore"}>
        <TabsList>
          <TabsTrigger value="mine" data-testid="tab-my-courses">Mis cursos {items.length > 0 && `(${items.length})`}</TabsTrigger>
          <TabsTrigger value="explore" data-testid="tab-explore-courses">Explorar</TabsTrigger>
        </TabsList>

        <TabsContent value="mine" className="mt-4">
          {items.length === 0 ? (
            <Card className="p-10 text-center" data-testid="my-courses-empty">
              <GraduationCap className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <div className="text-slate-700 font-medium">Aún no tenés cursos</div>
              <p className="text-sm text-slate-500 mt-1">Mirá la pestaña "Explorar" para descubrir los cursos disponibles.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((c) => (
                <Card key={c.id} className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/dashboard/my-courses/${c.id}`)} data-testid={`my-course-${c.id}`}>
                  <div className="relative aspect-video bg-slate-100">
                    {c.cover_url ? <img src={`${API}${c.cover_url}`} alt={c.title} className="absolute inset-0 h-full w-full object-cover" /> : (
                      <div className="absolute inset-0 flex items-center justify-center"><PlayCircle className="h-10 w-10 text-slate-300" /></div>
                    )}
                    {c.level && <Badge className="absolute top-2 left-2 bg-white/90 text-slate-700 border-0">{c.level}</Badge>}
                  </div>
                  <div className="p-4">
                    <div className="font-semibold text-slate-900 leading-snug">{c.title}</div>
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span>{c.completed_count}/{c.lesson_count} lecciones</span>
                        <span className="font-medium">{c.progress_pct}%</span>
                      </div>
                      <Progress value={c.progress_pct} className="h-2" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="explore" className="mt-4">
          {explore.length === 0 ? (
            <Card className="p-10 text-center text-sm text-slate-500" data-testid="explore-empty">
              ¡Ya tenés todos los cursos disponibles! Pronto publicaremos nuevos.
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {explore.map((c) => (
                <Card key={c.id} className="overflow-hidden flex flex-col" data-testid={`explore-course-${c.id}`}>
                  <div className="relative aspect-video bg-slate-100">
                    {c.cover_url ? <img src={`${API}${c.cover_url}`} alt={c.title} className="absolute inset-0 h-full w-full object-cover" loading="lazy" /> : (
                      <div className="absolute inset-0 flex items-center justify-center"><PlayCircle className="h-10 w-10 text-slate-300" /></div>
                    )}
                    {c.level && <Badge className="absolute top-2 left-2 bg-white/90 text-slate-700 border-0">{c.level}</Badge>}
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="font-semibold text-slate-900 leading-snug">{c.title}</div>
                    {c.subtitle && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.subtitle}</p>}
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-2">
                      <span className="inline-flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> {c.module_count} mód · {c.lesson_count} lec</span>
                      <span className="inline-flex items-center gap-1"><Award className="h-3.5 w-3.5" /> Certificado</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                      <div className="font-bold text-slate-900">{c.price === 0 ? <span className="text-emerald-600">Gratis</span> : `$${Number(c.price).toFixed(0)} USD`}</div>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => window.open(`/cursos/${c.id}`, "_blank")} data-testid={`explore-detail-${c.id}`}>Ver detalle</Button>
                        {c.price === 0 ? (
                          <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" disabled={enrolling === c.id} onClick={() => enrollFree(c)} data-testid={`explore-enroll-${c.id}`}>
                            {enrolling === c.id ? "…" : "Inscribirme"}
                          </Button>
                        ) : (
                          <Button size="sm" className="h-8 text-xs bg-orange-500 hover:bg-orange-600 text-white" onClick={() => { setOrderDone(false); setOrderCourse(c); }} data-testid={`explore-buy-${c.id}`}>
                            Lo quiero
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Paid course request dialog */}
      <Dialog open={!!orderCourse} onOpenChange={(o) => !o && setOrderCourse(null)}>
        <DialogContent className="max-w-sm" data-testid="student-order-dialog">
          <DialogTitle>{orderDone ? "¡Solicitud enviada! 🎉" : `Solicitar "${orderCourse?.title}"`}</DialogTitle>
          {orderDone ? (
            <div className="space-y-3 text-sm text-slate-600" data-testid="student-order-success">
              <p>Te contactaremos con las instrucciones de pago (transferencia o SINPE). Apenas confirmemos el pago, el curso aparecerá en "Mis cursos".</p>
              <Button className="w-full" onClick={() => setOrderCourse(null)}>Entendido</Button>
            </div>
          ) : (
            <div className="space-y-3 text-sm text-slate-600">
              <p>Precio: <strong>${Number(orderCourse?.price || 0).toFixed(0)} USD</strong> · pago único, acceso de por vida.</p>
              <p>Enviaremos las instrucciones de pago a <strong>{user?.email}</strong>.</p>
              <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white" onClick={requestCourse} disabled={ordering} data-testid="student-order-submit">
                {ordering ? "Enviando…" : "Solicitar acceso"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
